import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import api from "../../../api.js";
import "../../../CSS/Party_master.css";
import { useRights } from "../../RightsContext.jsx"; // adjust path to wherever RightsContext.jsx actually lives

// Swap in whatever Submenus.pathname holds for Party Master in your DB —
// leading slash / casing don't matter, both sides get normalized the same way.
const PARTY_MASTER_PATH = "/Party_Master"; // Example: "/master/party" or "/master/party-master"

// ── INITIAL DATA (Now empty, will be fetched from API) ────────────────
const INITIAL_PARTIES = [];

const INITIAL_BRANCHES = [
  { id: 1, code: "MUM", name: "Mumbai Central Hub" },
  { id: 2, code: "DEL", name: "Delhi NCR Depot" }
];

const bankMap = {
  HDFC: "HDFC Bank", SBIN: "State Bank of India", ICIC: "ICICI Bank", UTIB: "Axis Bank",
  PUNB: "Punjab National Bank", CNRB: "Canara Bank", BARB: "Bank of Baroda", KKBK: "Kotak Mahindra Bank"
};

// ── UI HELPERS ──────────────────────────────────
function Toast({ toast }) {
  if (!toast.show) return null;
  return createPortal(
    <div className={`toast show ${toast.type || ""}`} style={{ zIndex: 99999 }}>
      <span>{toast.msg}</span>
    </div>,
    document.body
  );
}

function statusBadge(s) {
  return (s === "active" || s === "A") ? (
    <span className="td-badge badge-active" style={{ background: "rgba(43,174,150,0.12)", color: "#2BAE96", padding: "3px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>● Active</span>
  ) : (
    <span className="td-badge badge-inactive" style={{ background: "rgba(255,107,107,0.1)", color: "#FF6B6B", padding: "3px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>● Inactive</span>
  );
}

function ActionButtons({ onEdit, onToggle, onDelete, status, rights }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      {rights.edit && (
        <button className="icon-btn edit" onClick={onEdit} title="Edit" style={{ width: 30, height: 30, borderRadius: 7, border: "1.5px solid #D8E8F5", background: "#FAFCFF", cursor: "pointer", color: "#8AA5C0", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
        </button>
      )}
      {/* {rights.edit && ( */}
        <button className="icon-btn toggle" onClick={onToggle} title={status === "active" ? "Deactivate" : "Activate"} style={{ width: 30, height: 30, borderRadius: 7, border: "1.5px solid #D8E8F5", background: "#FAFCFF", cursor: "pointer", color: "#8AA5C0", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M18.36 6.64a9 9 0 11-12.73 0" /><line x1="12" y1="2" x2="12" y2="12" /></svg>
        </button>
      
      {rights.delete && (
        <button className="icon-btn del" onClick={onDelete} title="Delete" style={{ width: 30, height: 30, borderRadius: 7, border: "1.5px solid #D8E8F5", background: "#FAFCFF", cursor: "pointer", color: "#8AA5C0", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /></svg>
        </button>
      )}
      {/* {!rights.edit && !rights.delete && <span style={{ color: "var(--text-muted)", fontSize: 11.5 }}>—</span>} */}
    </div>
  );
}

function F({ label, req, hint, children, cls }) {
  return (
    <div className={`field ${cls || ""}`} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--text-secondary)" }}>{label} {req && <span style={{ color: "#FF6B6B" }}>*</span>}</label>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function StatusSelect({ value, onChange }) {
  return (
    <F label="Status">
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
    </F>
  );
}

// ── RECORD BUILDING ──────────────────────────────
function buildInitialForm(rec, channels) {
  // Only for a brand-new party (rec is empty) do we default the toggle to
  // match the channel's global IsEnabled setting. An existing party's
  // notifSms/notifEmail/notifWa always comes back as an explicit true/false
  // from the fetch (never undefined), so this never overrides a party's
  // own previously saved preference — it only affects new parties that
  // haven't been configured yet.
  const isNewParty = !rec || Object.keys(rec).length === 0;
  const channelEnabled = (code) => {
    if (!channels || channels.length === 0) return true; // still loading
    const ch = channels.find(
      c => (c.channelCode || c.ChannelCode || "").toUpperCase() === code.toUpperCase()
    );
    return ch ? (ch.isEnabled ?? ch.IsEnabled ?? true) : false;
  };

  return {
    billing: rec.billing || false, code: rec.code || "", name: rec.name || "",
    contact: rec.contact || "", mobile: rec.mobile || "", email: rec.email || "",
    address: rec.address || "", city: rec.city || "", state: rec.state || "", pin: rec.pin || "",
    panNo: rec.panNo || "", fuelSurcharge: rec.fuelSurcharge || "",
    gstPercent: rec.gstPercent ?? "18", gstin: rec.gstin || "", gstType: rec.gstType || "R",
    billingBranch: rec.billingBranch || "", billingCycle: rec.billingCycle || "monthly",
    creditDays: rec.creditDays || 0, addCharges: rec.addCharges ? [...rec.addCharges] : [],
    accountName: rec.accountName || "", accountNo: rec.accountNo || "", accType: rec.accType || "current",
    ifsc: rec.ifsc || "", bankName: rec.bankName || "", bankBranch: rec.bankBranch || "",
    notifSms: rec.notifSms !== undefined ? rec.notifSms : (isNewParty ? channelEnabled("SMS") : false), smsNumber: rec.smsNumber || "",
    notifEmail: rec.notifEmail !== undefined ? rec.notifEmail : (isNewParty ? channelEnabled("EMAIL") : false), emailAddress: rec.emailAddress || "",
    notifWa: rec.notifWa !== undefined ? rec.notifWa : (isNewParty ? channelEnabled("WHATSAPP") : false), waNumber: rec.waNumber || "",
    selectedEvents: rec.selectedEvents || [],
    insActive: rec.insActive || false, insRiskType: rec.insRiskType || "Owner's Risk (OR)",
    insApp: rec.insApp || "Auto — apply to all consignments", insBasis: rec.insBasis || "% of Declared Value",
    insPremiumRate: rec.insPremiumRate || "0.5", insMinPremium: rec.insMinPremium || "100",
    insMaxCoverage: rec.insMaxCoverage || "500000", insChargedTo: rec.insChargedTo || "Consignor (Sender)",
    insGst: rec.insGst || "18% (Standard)", insRemarks: rec.insRemarks || "",

    portalActive: rec.portalActive || false,
    portalUsername: rec.portalUsername || "", portalPassword: rec.portalPassword || "",
    portalAccessLevel: rec.portalAccessLevel || "View Only",
    portalView1: rec.portalView1 !== undefined ? rec.portalView1 : false,
    portalView2: rec.portalView2 !== undefined ? rec.portalView2 : false,
    portalView3: rec.portalView3 !== undefined ? rec.portalView3 : false,
    portalView4: rec.portalView4 !== undefined ? rec.portalView4 : false,
    portalIps: rec.portalIps || "",

    apiActive: rec.apiActive || false,
    apiKey: rec.apiKey || "STL-YUDYzC3ySEoQlizWkwmyp8fRjh1X2WAm",
    apiSecret: rec.apiSecret || "xyzsecret",
    apiView1: rec.apiView1 !== undefined ? rec.apiView1 : false,
    apiView2: rec.apiView2 !== undefined ? rec.apiView2 : false,
    apiView3: rec.apiView3 !== undefined ? rec.apiView3 : false,
    apiView4: rec.apiView4 !== undefined ? rec.apiView4 : false,
    apiRateLimit: rec.apiRateLimit || "100 / hour",
    apiWebhook: rec.apiWebhook || "",
    apiIps: rec.apiIps || "",
  };
}

function buildRecord(form, id) {
  return {
    id: id || 0,
    code: form.code.toUpperCase(), name: form.name, billing: form.billing,
    contact: form.contact, mobile: form.mobile, email: form.email,
    address: form.address, city: form.city, state: form.state, pin: form.pin,
    panNo: form.panNo.toUpperCase(), fuelSurcharge: form.fuelSurcharge,
    gstPercent: form.gstPercent, gstin: form.gstin.toUpperCase(), gstType: form.gstType,
    billingBranch: form.billing ? form.billingBranch : "",
    billingCycle: form.billing ? form.billingCycle : "",
    creditDays: form.billing ? Number(form.creditDays) : 0,
    addCharges: form.billing ? form.addCharges : [],
    accountName: form.billing ? form.accountName : "", accountNo: form.billing ? form.accountNo : "",
    accType: form.billing ? form.accType : "current",
    ifsc: form.billing ? form.ifsc.toUpperCase() : "", bankName: form.billing ? form.bankName : "",
    bankBranch: form.billing ? form.bankBranch : "",
    notifSms: form.notifSms, smsNumber: form.smsNumber,
    notifEmail: form.notifEmail, emailAddress: form.emailAddress,
    notifWa: form.notifWa, waNumber: form.waNumber,
    selectedEvents: form.selectedEvents,
    insActive: form.insActive, insRiskType: form.insRiskType, insApp: form.insApp, insBasis: form.insBasis,
    insPremiumRate: form.insPremiumRate, insMinPremium: form.insMinPremium, insMaxCoverage: form.insMaxCoverage,
    insChargedTo: form.insChargedTo, insGst: form.insGst, insRemarks: form.insRemarks,
    portalActive: form.portalActive, portalUsername: form.portalUsername, portalPassword: form.portalPassword,
    portalAccessLevel: form.portalAccessLevel, portalView1: form.portalView1, portalView2: form.portalView2,
    portalView3: form.portalView3, portalView4: form.portalView4, portalIps: form.portalIps,
    apiActive: form.apiActive, apiKey: form.apiKey, apiSecret: form.apiSecret,
    apiView1: form.apiView1, apiView2: form.apiView2, apiView3: form.apiView3, apiView4: form.apiView4,
    apiRateLimit: form.apiRateLimit, apiWebhook: form.apiWebhook, apiIps: form.apiIps,
  };
}

// ── THE MODAL CONTAINING THE FORM ──────────────────────────────
function PartyModal({ modal, onClose, onSave, parties, states, branches, channels, events, mapPartyRecord }) {
  const isEdit = modal.id !== null;
  // Start from whatever we already have locally (instant paint, no blank flash),
  // then refetch this one party fresh from the server below.
  const localRec = isEdit ? parties.find((p) => p.id === modal.id) || {} : {};
  const [rec, setRec] = useState(localRec);
  const [loadingRec, setLoadingRec] = useState(isEdit);
  const [form, setForm] = useState(() => buildInitialForm(localRec, channels));

  // EDIT mode: fetch the latest data for this party_id from the server —
  // don't just trust whatever was last loaded into the grid, since it may
  // be stale (another user could have changed it since the grid loaded).
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    setLoadingRec(true);
    api.get(`/get-party/${modal.id}`)
      .then((response) => {
        if (cancelled) return;
        const fresh = mapPartyRecord(response.data);
        setRec(fresh);
        setForm(buildInitialForm(fresh, channels));
      })
      .catch((error) => {
        console.error("Failed to fetch party for edit:", error);
      })
      .finally(() => {
        if (!cancelled) setLoadingRec(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.id]);

  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  // If a channel gets disabled in ChannelMaster after this party's
  // preference was already saved as ON, force it off here too — otherwise
  // the card is hidden but the underlying true value could still get sent
  // on save, effectively re-enabling a channel that's supposed to be off.
  useEffect(() => {
    if (!isChannelEnabled("SMS") && form.notifSms) set("notifSms", false);
    if (!isChannelEnabled("EMAIL") && form.notifEmail) set("notifEmail", false);
    if (!isChannelEnabled("WHATSAPP") && form.notifWa) set("notifWa", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels]);

  // channels → card title by ChannelCode
  // const getChannelTitle = (channelCode, fallback) => {
  //   const ch = channels?.find(c => (c.ChannelCode || c.channelCode) === channelCode);
  //   return ch ? (ch.ChannelName || ch.channelName || fallback) : fallback;
  // };

  // // events filtered by ChannelCode → checkboxes
  // const getEventsByChannel = (channelCode) => {
  //   return (events || []).filter(e => (e.ChannelCode || e.channelCode) === channelCode);
  // };
const getChannelTitle = (channelId, fallback) => {
  const ev = events?.find(e => e.channelId === channelId);
  return ev ? (ev.channelName || fallback) : fallback;
};

// Checks whether a channel is enabled, based on /get-channels' response.
// IMPORTANT: this API only returns ENABLED channels — a disabled channel
// (like EMAIL with IsEnabled=false) is left OUT of the response entirely,
// it does not come back with isEnabled:false. So "not found in the list"
// means disabled, NOT "still loading" — except on the very first render
// before the fetch has completed at all (channels.length === 0), where we
// default to true just to avoid every card flashing hidden for a moment.
const isChannelEnabled = (channelCode) => {
  if (!channels || channels.length === 0) return true; // still loading
  const ch = channels.find(
    c => (c.channelCode || c.ChannelCode || "").toUpperCase() === channelCode.toUpperCase()
  );
  return ch ? (ch.isEnabled ?? ch.IsEnabled ?? true) : false; // not in list = disabled
};

const getEventsByChannel = (channelId) => {
  return (events || []).filter(e => e.channelId === channelId);
};
  const toggleEvent = (eventId) => {
    setForm(f => {
      const isSelected = f.selectedEvents.includes(eventId);
      if (isSelected) {
        return { ...f, selectedEvents: f.selectedEvents.filter(id => id !== eventId) };
      }
      return { ...f, selectedEvents: [...f.selectedEvents, eventId] };
    });
  };

  const addCharge = () => set("addCharges", [...form.addCharges, { name: "", type: "fixed", amt: 0 }]);
  const removeCharge = (i) => set("addCharges", form.addCharges.filter((_, idx) => idx !== i));
  const updateCharge = (i, field, val) => {
    const ac = [...form.addCharges];
    ac[i] = { ...ac[i], [field]: field === "amt" ? parseFloat(val) || 0 : val };
    set("addCharges", ac);
  };
  const handleIfsc = (val) => {
    set("ifsc", val.toUpperCase());
    if (val.length === 11) {
      const prefix = val.slice(0, 4).toUpperCase();
      if (bankMap[prefix]) set("bankName", bankMap[prefix]);
    }
  };

  function handleSaveClick() {
    if (!form.code || !form.name) return alert("Code and Name are required.");

    if (!/^[A-Z0-9]{1,3}$/.test(form.code)) {
      return alert("Party Code must be alphanumeric only, up to 3 characters (no special characters).");
    }

    if (form.mobile && !/^\d{10}$/.test(form.mobile)) {
      return alert("Mobile number must be exactly 10 digits (numbers only).");
    }

    if (form.panNo && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.panNo)) {
      return alert("PAN Number must be in the format AAAAA9999A (5 letters, 4 digits, 1 letter) — no special characters.");
    }

    if (form.pin && !/^\d{6}$/.test(form.pin)) {
      return alert("Pincode must be exactly 6 digits (numbers only).");
    }

    if (form.billing) {
      if (!form.gstin) return alert("GSTIN is required when Party is marked as Billing.");
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gstin)) {
        return alert("GSTIN must be a valid 15-character GST number (e.g. 27ABCDE1234F1Z5) — no special characters.");
      }
    } else if (form.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gstin)) {
      return alert("GSTIN must be a valid 15-character GST number (e.g. 27ABCDE1234F1Z5) — no special characters.");
    }

    if (form.billing) {
      if (!form.billingBranch) return alert("Billing Branch is required when Party is marked as Billing.");
      if (!form.billingCycle) return alert("Billing Cycle is required when Party is marked as Billing.");
    }

    if (form.notifSms) {
      if (!form.smsNumber) return alert("SMS Number is required when SMS notifications are enabled.");
      if (!/^\d{10}$/.test(form.smsNumber)) return alert("SMS Number must be exactly 10 digits (numbers only).");
    }

    if (form.notifWa) {
      if (!form.waNumber) return alert("WhatsApp Number is required when WhatsApp notifications are enabled.");
      if (!/^\d{10}$/.test(form.waNumber)) return alert("WhatsApp Number must be exactly 10 digits (numbers only).");
    }

    onSave(buildRecord(form, modal.id));
  }

  return createPortal(
    <div className="overlay show" style={{ display: "flex", position: "fixed", inset: 0, background: "rgba(20,40,70,0.45)", backdropFilter: "blur(6px)", zIndex: 500, alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="modal modal-wide" style={{ background: "var(--surface)", borderRadius: 18, boxShadow: "var(--shadow-lg)", width: "100%", maxWidth: 860, maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>

        <div className="modal-head" style={{ padding: "22px 24px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}>{isEdit ? "Edit Party" : "Add Party"}</h3>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--warm-white)", cursor: "pointer" }}>✕</button>
        </div>

        <div className="modal-body" style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          {loadingRec && (
            <div style={{ padding: "8px 12px", borderRadius: 8, background: "#F0F6FF", color: "#3468C0", fontSize: 12.5 }}>
              Loading latest party details…
            </div>
          )}
          {/* THE EXTRACTED FORM */}
          <div className="billing-toggle-row" style={{ marginBottom: 4 }}>
            <div className="btog-left">
              <div className="btog-icon">💳</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>Billing Party</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Enable to allow invoicing, billing cycle & payment configuration</div>
              </div>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" checked={form.billing} onChange={(e) => set("billing", e.target.checked)} />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="cond-divider"><span>Basic Details</span></div>
          <div className="grid-3">
            <F label="Party Code" req><input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3))} placeholder="e.g. TML" maxLength={3} style={{ textTransform: "uppercase", padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
            <F label="Party / Company Name" req cls="col-2"><input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full legal name" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
            <F label="Contact Person"><input value={form.contact} onChange={(e) => set("contact", e.target.value)} placeholder="Name" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
            <F label="Mobile"><input value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit" maxLength={10} inputMode="numeric" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
            <F label="Email"><input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@company.com" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>

            <div className="field" style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", color: "var(--text-secondary)" }}>Address</label>
              <textarea value={form.address} onChange={(e) => set("address", e.target.value)} rows={3} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", resize: "vertical" }} />
            </div>
            <F label="City"><input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="City" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
            <F label="State">
              <select value={form.state} onChange={(e) => set("state", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                <option value="">— Select State —</option>
                {states && states.map(s => <option key={s.State_id} value={s.State_id}>{s.State_Name}</option>)}
              </select>
            </F>

            <F label="Pincode"><input value={form.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit PIN" maxLength={6} inputMode="numeric" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
            <F label="PAN Number"><input value={form.panNo} onChange={(e) => set("panNo", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))} placeholder="e.g. AAAAA9999A" maxLength={10} style={{ textTransform: "uppercase", padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
            <F label="Fuel Surcharge (%)"><input type="number" value={form.fuelSurcharge} onChange={(e) => set("fuelSurcharge", e.target.value)} placeholder="e.g. 15" min={0} max={100} step="0.01" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
          </div>


          <div className="cond-divider"><span>GST Details</span></div>
          {form.billing && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(249,168,37,0.1)", border: "1px solid rgba(249,168,37,0.3)", borderRadius: 8, padding: "9px 13px", fontSize: 12.5, color: "#c67c00", marginBottom: 10 }}>
              ⚠ GST details are mandatory for Billing Parties
            </div>
          )}
          <div className="grid-3">
            <F label="GST (%)">
              <select value={form.gstPercent} onChange={(e) => set("gstPercent", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                <option value="0">0%</option>
                <option value="5">5%</option>
                <option value="12">12%</option>
                <option value="18">18%</option>
                <option value="28">28%</option>
              </select>
            </F>
            <F label="GSTIN" req={form.billing}><input value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15))} placeholder="e.g. 27ABCDE1234F1Z5" maxLength={15} style={{ textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, fontSize: 12.5, padding: "9px 10px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
            <F label="GST Registration Type">
              <select value={form.gstType} onChange={(e) => set("gstType", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                <option value="R">Regular</option>
                <option value="C">Composition</option>
                <option value="U">Unregistered</option>
                <option value="S">SEZ</option>
              </select>
            </F>
          </div>

          {form.billing && (
            <div id="billing-party-section">
              <div className="cond-divider"><span>Billing Configuration</span></div>
              <div className="grid-3">
                <F label="Billing Branch" req hint="Branch that raises invoices for this party">
                  <select value={form.billingBranch} onChange={e => set("billingBranch", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                    <option value="">— Select Branch —</option>
                    {branches && branches.map(b => (
                      <option key={b.Branch_id} value={b.Branch_id}>
                        {b.Branch_Name}
                      </option>
                    ))}
                  </select>
                </F>
                <F label="Billing Cycle" req>
                  <select value={form.billingCycle} onChange={e => set("billingCycle", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                    <option value="weekly">Weekly (every Monday)</option>
                    <option value="fortnightly">Fortnightly (1st & 16th)</option>
                    <option value="monthly">Monthly (1st of month)</option>
                    <option value="ondemand">On-Demand / Per Consignment</option>
                  </select>
                </F>
                <F label="Credit Days" hint="Payment due after billing date">
                  <input type="number" value={form.creditDays} onChange={e => set("creditDays", e.target.value)} placeholder="0" min="0" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                </F>
              </div>

              <div className="cond-divider"><span>Additional Charges per Consignment</span></div>
              <div className="cond-sub-block">
                <div className="csb-header" style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "var(--sky)", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm) var(--radius-sm) 0 0", fontSize: 13, color: "var(--text-secondary)" }}>
                  <span className="csb-icon">➕</span> Charges applied automatically to each consignment for this party
                </div>
                <div style={{ overflowX: "auto", border: "1.5px solid var(--border)", borderTop: "none", borderRadius: "0 0 var(--radius-sm) var(--radius-sm)" }}>
                  <table className="addc-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "var(--warm-white)", borderBottom: "1px solid var(--border)" }}>
                        <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", minWidth: 160 }}>Charge Name</th>
                        <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", minWidth: 120 }}>Type</th>
                        <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", minWidth: 90 }}>Amount</th>
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.addCharges.map((c, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "8px 12px" }}><input value={c.name} onChange={e => updateCharge(i, "name", e.target.value)} placeholder="e.g. Handling Charges" style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 6 }} /></td>
                          <td style={{ padding: "8px 12px" }}>
                            <select value={c.type} onChange={e => updateCharge(i, "type", e.target.value)} style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 6 }}>
                              <option value="fixed">Fixed (₹)</option>
                              <option value="pct">Percentage (%)</option>
                            </select>
                          </td>
                          <td style={{ padding: "8px 12px" }}><input type="number" value={c.amt} onChange={e => updateCharge(i, "amt", e.target.value)} placeholder="0" style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 6 }} /></td>
                          <td style={{ padding: "8px 12px", textAlign: "center" }}>
                            <button type="button" className="addc-del" onClick={() => removeCharge(i)} style={{ background: "none", border: "none", color: "#FF6B6B", cursor: "pointer", fontSize: 16 }}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="addc-add" style={{ marginTop: 10 }}>
                  <button type="button" className="addc-add-btn" onClick={addCharge} style={{ background: "white", border: "1.5px dashed var(--blue)", color: "var(--blue)", padding: "7px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>+ Add Charge</button>
                </div>
              </div>

              <div className="cond-divider"><span>Default Bank Account for Payment Receipt</span></div>
              <div className="bank-block" style={{ border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "hidden", background: "var(--warm-white)" }}>
                <div className="bank-header" style={{ padding: "12px 16px", background: "var(--sky)", borderBottom: "1px solid var(--border)", fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>🏦 Bank Account Details</div>
                <div className="grid-3" style={{ padding: 16 }}>
                  <F label="Account Name" req><input value={form.accountName} onChange={e => set("accountName", e.target.value)} placeholder="As per bank records" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
                  <F label="Account Number" req><input value={form.accountNo} onChange={e => set("accountNo", e.target.value)} placeholder="Account number" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
                  <F label="Account Type">
                    <select value={form.accType} onChange={e => set("accType", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                      <option value="current">Current Account</option>
                      <option value="savings">Savings Account</option>
                    </select>
                  </F>
                  <F label="IFSC Code" req><input value={form.ifsc} onChange={e => handleIfsc(e.target.value)} placeholder="e.g. HDFC0000001" maxLength={11} style={{ textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600, padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
                  <F label="Bank Name" req><input value={form.bankName} onChange={e => set("bankName", e.target.value)} placeholder="Auto-filled from IFSC" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
                  <F label="Bank Branch"><input value={form.bankBranch} onChange={e => set("bankBranch", e.target.value)} placeholder="Branch name" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} /></F>
                </div>
              </div>
            </div>
          )}

          <div className="cond-divider"><span>Notification Preferences</span></div>
          <div className="notif-config-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 2 }}>

            {isChannelEnabled("SMS") && (
            <div className="nc-card" style={{ border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "hidden", background: "var(--warm-white)", transition: "border-color .2s" }}>
              <div className="nc-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 13px", background: "var(--sky)", borderBottom: "1px solid var(--border)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="nc-icon sms" style={{ width: 30, height: 30, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, background: "rgba(58,123,213,0.12)" }}>📱</div>
                  <div className="nc-title" style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{getChannelTitle("SMS", "SMS")}</div>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={form.notifSms} onChange={e => set("notifSms", e.target.checked)} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className={`nc-body ${!form.notifSms ? "hidden" : ""}`} style={{ padding: "12px 13px", display: form.notifSms ? "flex" : "none", flexDirection: "column", gap: 9 }}>
                <F label="SMS Number">
                  <input value={form.smsNumber} onChange={e => set("smsNumber", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit number" maxLength={10} inputMode="numeric" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                </F>
                <div className="nc-events" style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 4 }}>
                  
                    {getChannelTitle(1, "SMS")}
                     {getEventsByChannel(1).map(e => {
  const id = e.eventId;
  const name = e.eventName;


                    return (
                      <label key={id} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer", padding: "2px 0" }}>
                        <input type="checkbox" checked={form.selectedEvents.includes(id)} onChange={() => toggleEvent(id)} style={{ width: 13, height: 13, accentColor: "var(--teal)", cursor: "pointer" }} /> {name}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            )}

            {isChannelEnabled("EMAIL") && (
            <div className="nc-card" style={{ border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "hidden", background: "var(--warm-white)", transition: "border-color .2s" }}>
              <div className="nc-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 13px", background: "var(--sky)", borderBottom: "1px solid var(--border)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="nc-icon email" style={{ width: 30, height: 30, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, background: "rgba(249,168,37,0.12)" }}>✉️</div>
                  <div className="nc-title" style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{getChannelTitle("EMAIL", "Email")}</div>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={form.notifEmail} onChange={e => set("notifEmail", e.target.checked)} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className={`nc-body ${!form.notifEmail ? "hidden" : ""}`} style={{ padding: "12px 13px", display: form.notifEmail ? "flex" : "none", flexDirection: "column", gap: 9 }}>
                <F label="Email Address">
                  <input value={form.emailAddress} onChange={e => set("emailAddress", e.target.value)} placeholder="email@domain.com" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                </F>
                <div className="nc-events" style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 4 }}>
                  {getChannelTitle(3, "Email")}

                  {getEventsByChannel(3).map(e => {
  const id = e.eventId;
  const name = e.eventName;
                    
                    return (
                      <label key={id} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer", padding: "2px 0" }}>
                        <input type="checkbox" checked={form.selectedEvents.includes(id)} onChange={() => toggleEvent(id)} style={{ width: 13, height: 13, accentColor: "var(--teal)", cursor: "pointer" }} /> {name}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            )}

            {isChannelEnabled("WHATSAPP") && (
            <div className="nc-card" style={{ border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "hidden", background: "var(--warm-white)", transition: "border-color .2s", gridRow: "span 2" }}>
              <div className="nc-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 13px", background: "var(--sky)", borderBottom: "1px solid var(--border)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="nc-icon wa" style={{ width: 30, height: 30, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, background: "rgba(37,211,102,0.12)" }}>💬</div>
                  <div className="nc-title" style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{getChannelTitle("WhatsApp", "WhatsApp")}</div>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={form.notifWa} onChange={e => set("notifWa", e.target.checked)} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className={`nc-body ${!form.notifWa ? "hidden" : ""}`} style={{ padding: "12px 13px", display: form.notifWa ? "flex" : "none", flexDirection: "column", gap: 9 }}>
                <F label="WhatsApp Number">
                  <input value={form.waNumber} onChange={e => set("waNumber", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit number" maxLength={10} inputMode="numeric" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                </F>
                <div className="nc-events" style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 4 }}>
                 {getChannelTitle(2, "WhatsApp")}
{getEventsByChannel(2).map(e => {
  const id = e.eventId;
  const name = e.eventName;

                    return (
                      <label key={id} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer", padding: "2px 0" }}>
                        <input type="checkbox" checked={form.selectedEvents.includes(id)} onChange={() => toggleEvent(id)} style={{ width: 13, height: 13, accentColor: "var(--teal)", cursor: "pointer" }} /> {name}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            )}

          </div>

          <div className="cond-divider"><span>Consignment Insurance</span></div>
          <div style={{ border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--warm-white)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "var(--sky)", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(58,123,213,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>🛡️</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>Consignment Insurance</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>Configure insurance rules applied to every consignment for this party</div>
                </div>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={form.insActive} onChange={e => set("insActive", e.target.checked)} />
                <span className="toggle-slider"></span>
              </label>
            </div>
            {form.insActive && (
              <div style={{ padding: "16px 20px" }}>
                <div className="grid-3">
                  <F label="Risk Type" req hint="OR — party bears risk. CR — carrier liable. Third-party — insurer covers.">
                    <select value={form.insRiskType} onChange={e => set("insRiskType", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                      <option>Owner's Risk (OR)</option>
                      <option>Carrier's Risk (CR)</option>
                    </select>
                  </F>
                  <F label="Insurance Application">
                    <select value={form.insApp} onChange={e => set("insApp", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                      <option>Auto — apply to all consignments</option>
                      <option>Manual — optional on booking</option>
                    </select>
                  </F>
                  <F label="Premium Basis" req>
                    <select value={form.insBasis} onChange={e => set("insBasis", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                      <option>% of Declared Value</option>
                      <option>Fixed Flat Rate per Trip</option>
                    </select>
                  </F>

                  <F label="Premium Rate" req hint="e.g. 0.5% of declared value per consignment">
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                      <input value={form.insPremiumRate} onChange={e => set("insPremiumRate", e.target.value)} placeholder="0.5" style={{ padding: "9px 30px 9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                      <span style={{ position: "absolute", right: 12, color: "var(--text-secondary)", fontSize: 13, fontWeight: 600 }}>%</span>
                    </div>
                  </F>
                  <F label="Minimum Premium (₹)" hint="Charged even if calculated amount is lower">
                    <input type="number" value={form.insMinPremium} onChange={e => set("insMinPremium", e.target.value)} placeholder="100" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                  </F>
                  <F label="Maximum Coverage (₹)" hint="Upper limit of insurance payout">
                    <input type="number" value={form.insMaxCoverage} onChange={e => set("insMaxCoverage", e.target.value)} placeholder="500000" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                  </F>

                  <F label="Premium Charged To">
                    <select value={form.insChargedTo} onChange={e => set("insChargedTo", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                      <option>Consignor (Sender)</option>
                      <option>Consignee (Receiver)</option>
                      <option>Third Party</option>
                    </select>
                  </F>
                  <F label="GST on Insurance Premium">
                    <select value={form.insGst} onChange={e => set("insGst", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                      <option>18% (Standard)</option>
                      <option>Exempt</option>
                    </select>
                  </F>
                  <F label="Exclusions / Remarks" hint="e.g. Glass, liquids, perishables excluded. Theft only with FIR copy.">
                    <textarea value={form.insRemarks} onChange={e => set("insRemarks", e.target.value)} rows={2} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", minHeight: 40 }} />
                  </F>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,107,107,0.06)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 8, padding: "10px 14px", marginTop: 18 }}>
                  <div style={{ color: "#4f75b8", fontSize: 13 }}>🛡️</div>
                  <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>Configure fields above to see summary.</div>
                </div>
              </div>
            )}
          </div>

          <div className="cond-divider"><span>Portal Access</span></div>
          <div style={{ border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--warm-white)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "var(--sky)", borderBottom: form.portalActive ? "1px solid var(--border)" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(58,123,213,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>🌐</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>Customer Portal Access</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>Party can log in to view consignment status, download LR copies & PODs</div>
                </div>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={form.portalActive} onChange={e => set("portalActive", e.target.checked)} />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {form.portalActive && (
              <>
                <div style={{ padding: "16px 20px" }}>
                  <div className="grid-3">
                    <F label="Portal Username" req>
                      <input value={form.portalUsername} onChange={e => set("portalUsername", e.target.value)} placeholder="e.g. tata.motors" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                    </F>
                    <F label="Password" req>
                      <input type="text" value={form.portalPassword} onChange={e => set("portalPassword", e.target.value)} placeholder="Set password" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                    </F>
                    <F label="Access Level">
                      <select value={form.portalAccessLevel} onChange={e => set("portalAccessLevel", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                        <option>View Only</option>
                        <option>Admin</option>
                      </select>
                    </F>

                    <F label="Allowed Views">
                      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", textTransform: "uppercase" }}>
                          <input type="checkbox" checked={form.portalView1} onChange={e => set("portalView1", e.target.checked)} style={{ width: 14, height: 14, accentColor: "var(--blue)" }} /> MY CONSIGNMENTS
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", textTransform: "uppercase" }}>
                          <input type="checkbox" checked={form.portalView2} onChange={e => set("portalView2", e.target.checked)} style={{ width: 14, height: 14, accentColor: "var(--blue)" }} /> LIVE TRACKING
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", textTransform: "uppercase" }}>
                          <input type="checkbox" checked={form.portalView3} onChange={e => set("portalView3", e.target.checked)} style={{ width: 14, height: 14, accentColor: "var(--blue)" }} /> INVOICES & LEDGER
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", textTransform: "uppercase" }}>
                          <input type="checkbox" checked={form.portalView4} onChange={e => set("portalView4", e.target.checked)} style={{ width: 14, height: 14, accentColor: "var(--blue)" }} /> POD DOWNLOADS
                        </label>
                      </div>
                    </F>

                    <F label="Allowed IP Addresses" hint="(LEAVE BLANK TO ALLOW ALL)" cls="col-2">
                      <input value={form.portalIps} onChange={e => set("portalIps", e.target.value)} placeholder="e.g. 192.168.1.1, 10.0.0.0/24" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                    </F>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", background: "#f8fafd", borderTop: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 13, color: "var(--text-secondary)", fontWeight: 500 }}>
                    🔗 Portal URL: <strong style={{ color: "var(--text-primary)" }}>https://portal.saamitrade.in/login</strong>
                  </div>
                  <button type="button" style={{ background: "white", border: "1px solid var(--border)", padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", boxShadow: "var(--shadow-sm)" }}>
                    <span style={{ color: "var(--blue)" }}>📨</span> Send Invite
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="cond-divider"><span>API Access</span></div>
          <div style={{ border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--warm-white)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "var(--sky)", borderBottom: form.apiActive ? "1px solid var(--border)" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(43,174,150,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>⚙️</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>API Integration Access</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>Party can book consignments, track shipments & pull reports via REST API</div>
                </div>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={form.apiActive} onChange={e => set("apiActive", e.target.checked)} />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {form.apiActive && (
              <>
                <div style={{ padding: "16px 20px" }}>
                  <div className="grid-2">
                    <F label="API Key (Auto-Generated)">
                      <div style={{ display: "flex", gap: 8 }}>
                        <input value={form.apiKey} readOnly style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", fontFamily: "monospace", fontSize: 13, background: "#f4f6fa" }} />
                        <button type="button" style={{ padding: "0 12px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", background: "white", cursor: "pointer", fontWeight: 600, fontSize: 12.5, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 5 }}>
                          ↻ Generate
                        </button>
                      </div>
                    </F>
                    <F label="API Secret (Auto-Generated)">
                      <div style={{ display: "flex", gap: 8 }}>
                        <input type="password" value={form.apiSecret} readOnly style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", fontFamily: "monospace", fontSize: 13, background: "#f4f6fa" }} />
                        <button type="button" style={{ padding: "0 12px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", background: "white", cursor: "pointer", fontWeight: 600, fontSize: 12.5, color: "var(--text-secondary)", display: "flex", alignItems: "center" }}>
                          👁️
                        </button>
                      </div>
                    </F>
                  </div>

                  <div className="grid-3" style={{ marginTop: 14 }}>
                    <F label="Allowed Operations">
                      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", textTransform: "uppercase" }}>
                          <input type="checkbox" checked={form.apiView1} onChange={e => set("apiView1", e.target.checked)} style={{ width: 14, height: 14, accentColor: "var(--teal)" }} /> BOOK CONSIGNMENT
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", textTransform: "uppercase" }}>
                          <input type="checkbox" checked={form.apiView2} onChange={e => set("apiView2", e.target.checked)} style={{ width: 14, height: 14, accentColor: "var(--teal)" }} /> TRACK & STATUS
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", textTransform: "uppercase" }}>
                          <input type="checkbox" checked={form.apiView3} onChange={e => set("apiView3", e.target.checked)} style={{ width: 14, height: 14, accentColor: "var(--teal)" }} /> CANCEL CONSIGNMENT
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", textTransform: "uppercase" }}>
                          <input type="checkbox" checked={form.apiView4} onChange={e => set("apiView4", e.target.checked)} style={{ width: 14, height: 14, accentColor: "var(--teal)" }} /> REPORTS & ANALYTICS
                        </label>
                      </div>
                    </F>
                    <F label="Rate Limit (Calls/Hour)">
                      <select value={form.apiRateLimit} onChange={e => set("apiRateLimit", e.target.value)} style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", outline: "none" }}>
                        <option>100 / hour</option>
                        <option>1000 / hour</option>
                        <option>Unlimited</option>
                      </select>
                    </F>
                    <F label="Webhook URL (Optional)">
                      <input value={form.apiWebhook} onChange={e => set("apiWebhook", e.target.value)} placeholder="https://yourapp.com/webhook" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                    </F>
                  </div>

                  <div className="grid-3" style={{ marginTop: 14 }}>
                    <F label="Allowed IP Whitelist" hint="(LEAVE BLANK TO ALLOW ALL)" cls="col-2">
                      <input value={form.apiIps} onChange={e => set("apiIps", e.target.value)} placeholder="e.g. 203.0.113.0, 10.0.0.0/24" style={{ padding: "9px 13px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%" }} />
                    </F>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", background: "#f8fafd", borderTop: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 13, color: "var(--text-secondary)", fontWeight: 500 }}>
                    📖 API Docs: <strong style={{ color: "var(--text-primary)" }}>https://api.saamitrade.in/docs</strong>
                  </div>
                  <button type="button" style={{ background: "white", border: "1px solid var(--border)", padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", boxShadow: "var(--shadow-sm)" }}>
                    <span style={{ color: "var(--text-primary)" }}>⚡</span> Test Connection
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="cond-divider"><span>Record Status</span></div>
          <StatusSelect value={form.status} onChange={(v) => set("status", v)} />
        </div>

        <div className="modal-foot" style={{ padding: "16px 24px", borderTop: "1px solid var(--border)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding: "10px 20px", borderRadius: 9, background: "transparent", color: "var(--text-secondary)", border: "1.5px solid var(--border)", cursor: "pointer", fontWeight: 600 }}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSaveClick} style={{ padding: "10px 20px", borderRadius: 9, background: "linear-gradient(135deg, #3A7BD5, #2A5A9E)", color: "white", border: "none", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 7, boxShadow: "0 4px 14px rgba(58,123,213,0.35)" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
            Save
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}

// ── STANDALONE PARTY MASTER PAGE ──────────────────────────────
export default function PartyMaster() {
  const rights = useRights(PARTY_MASTER_PATH);
  const [parties, setParties] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState({ open: false, id: null });
  const [toast, setToast] = useState({ show: false, msg: "", type: "" });
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [channels, setChannels] = useState([]);   // notify.Channel
  const [events, setEvents] = useState([]);        // notify.Event

  
  function showToast(msg, type = "success") {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast({ show: false, msg: "", type: "" }), 3000);
  }

  useEffect(() => {
    fetchParties();
    fetchStates();
    fetchBranches();
    fetchChannels();
    fetchEvents();
  }, []);

  // fetch notify.Channel  → card titles
  const fetchChannels = async () => {
    try {
      const response = await api.get('/get-channels');
      setChannels(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Failed to fetch channels:", error);
    }
  };

  // fetch notify.Event  → checkboxes per channel
  const fetchEvents = async () => {
    try {
      const response = await api.get('/get-standard-notifications');
      setEvents(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Failed to fetch events:", error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await api.get('/dropdown?mode=STE');
      setStates(response.data);
    } catch (error) {
      console.error("Failed to fetch states:", error);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await api.get('/dropdown?mode=BRC');
      setBranches(response.data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const mapPartyRecord = (p) => ({
    id: p.party_id || p.Party_id,
    code: p.party_Code || p.Party_Code || "",
    name: p.party_Name || p.Party_Name || "",
    billing: (p.billing_Party || p.Billing_Party) === "O",
    contact: p.contact_Person || p.Contact_Person || "",
    mobile: p.mobile_No || p.Mobile_No || "",
    email: p.party_Email || p.Party_Email || "",
    address: p.Address || p.address || "",
    city: p.party_City || p.Party_City || "",
    state: p.state_id || p.State_id || "",
    pin: p.pincode || p.Pincode || "",
    panNo: p.pan_No || p.Pan_No || "",
    fuelSurcharge: p.fuel_Surcharge || p.Fuel_Surcharge || "",
    gstPercent: p.gsT_Perc ?? p.gsT_Perc ?? p.gsT_Perc ?? p.gsT_Perc ?? "18",
    gstin: p.gstin || "",
    gstType: p.gsT_Type ?? p.gst_Type ?? p.GST_Type ?? "R",
    billingBranch: p.billing_Branch_Id || p.Billing_Branch_Id || "",
    billingCycle: p.billing_Cycle || p.Billing_Cycle || "monthly",
    notifEmail: (p.notifications || p.Notifications || []).some(n => (n.event_id || n.Event_id) === 3) || (p.notification_Preference || p.Notification_Preference) === "E",
    notifSms: (p.notifications || p.Notifications || []).some(n => (n.event_id || n.Event_id) === 1) || (p.notification_Preference || p.Notification_Preference) === "S",
    notifWa: (p.notifications || p.Notifications || []).some(n => (n.event_id || n.Event_id) === 2) || (p.notification_Preference || p.Notification_Preference) === "W",
    status: "active",
    selectedEvents: (p.notifications || p.Notifications || []).map(n => n.notification_event_id || n.Notification_Event_id || n.EventId || n.eventId)
  });

  const fetchParties = async () => {
    try {
      const response = await api.get('/get-parties');
      const mapped = response.data.map(mapPartyRecord);
      setParties(mapped);
    } catch (error) {
      console.error("Failed to fetch parties:", error);
    }
  };

  const handleSave = async (record) => {
    try {
      let pref = 'N';
      if (record.notifEmail) pref = 'E';
      else if (record.notifWa) pref = 'W';
      else if (record.notifSms) pref = 'S';

      const mappedNotifications = [];
      (record.selectedEvents || []).forEach(eventId => {
        const evObj = events.find(e => Number(e.EventId || e.eventId) === Number(eventId));
        if (!evObj) return;

        const channelCode = evObj.ChannelCode || evObj.channelCode;
        const channelId   = Number(evObj.ChannelId || evObj.channelId);

        // Skip if that channel toggle is OFF
        if (channelCode === "SMS"   && !record.notifSms)   return;
        if (channelCode === "EMAIL" && !record.notifEmail) return;
        if (channelCode === "WHATSAPP" && !record.notifWa) return;

        mappedNotifications.push({
          event_id: channelId,
          notification_event_id: eventId
        });
      });

      const payload = {
        Party_id: record.id || 0,
        Billing_Party: record.billing ? "O" : "F",
        Party_Name: record.name,
        Party_Code: record.code,
        Mobile_No: record.mobile,
        Party_Email: record.email,
        Address: record.address,
        Party_City: record.city,
        state_id: Number(record.state) || 0,
        Pincode: record.pin,
        Pan_No: record.panNo,
        Fuel_Surcharge: record.fuelSurcharge !== "" ? Number(record.fuelSurcharge) : null,
        Contact_Person: record.contact,
        GST_Type: record.gstType,
        GST_Perc: record.gstPercent !== "" ? Number(record.gstPercent) : null,
        GSTIN: record.gstin,
        Billing_Branch_Id: record.billingBranch ? Number(record.billingBranch) : 0,
        Notification_Preference: pref,
        Notifications: mappedNotifications
      };

      if (record.id) {
        // EDIT — update the existing party, party_id wise.
        // Adjust the endpoint path/verb to match your backend route if it differs.
        await api.post(`/update-party/${record.id}`, payload);
      } else {
        // ADD — create a new party
        await api.post('/insert-party', [payload]);
      }

      showToast(record.id ? "Party updated successfully" : "Party saved successfully");
      fetchParties();
      setModal({ open: false, id: null });
    } catch (error) {
      showToast("Error saving party", "error");
    }
  };

  function handleToggleStatus(id) {
    setParties(prev => prev.map(p => {
      if (p.id === id) {
        return { ...p, status: p.status === "active" ? "inactive" : "active" };
      }
      return p;
    }));
    showToast("Status updated");
  }

  function handleDelete(id) {
    if (window.confirm("Delete this party?")) {
      setParties(prev => prev.filter(p => p.id !== id));
      showToast("Party deleted", "error");
    }
  }

  const roleFilterFn = r => filter === "all" || (filter === "billing" && r.billing) || (filter === "nonbilling" && !r.billing) || (filter === "active" && r.status === "active");

  const filtered = parties.filter(p => {
    const text = Object.values(p).join(" ").toLowerCase();
    return (!search || text.includes(search.toLowerCase())) && roleFilterFn(p);
  });

  return (
    <div style={{ padding: "24px 28px" }}>
      <Toast toast={toast} />

      <div className="panel-header" style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <div className="panel-title">
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700 }}>Party Master</h2>
          <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 3 }}>Single master for Billing Parties, Consignors & Consignees</p>
        </div>
        {rights.add && (
          <button onClick={() => setModal({ open: true, id: null })} style={{ background: "linear-gradient(135deg, #3A7BD5, #2A5A9E)", color: "white", padding: "10px 20px", borderRadius: 9, fontSize: 13.5, fontWeight: 600, border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 7, boxShadow: "0 4px 14px rgba(58,123,213,0.35)" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><path d="M12 5v14M5 12h14" /></svg> Add Party
          </button>
        )}
      </div>

      <div className="search-row" style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <div className="search-box" style={{ flex: 1, position: "relative" }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
          </span>
          <input type="text" placeholder="Search party master…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: "100%", padding: "10px 14px 10px 38px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", outline: "none" }} />
        </div>

        {[{ val: "all", label: "All" }, { val: "billing", label: "💳 Billing" }, { val: "nonbilling", label: "🏢 Non-Billing" }, { val: "active", label: "✓ Active" }].map(f => (
          <span key={f.val} className={`filter-chip ${filter === f.val ? "active" : ""}`} onClick={() => setFilter(f.val)} style={{ padding: "9px 14px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: 13, fontWeight: 600, background: filter === f.val ? "rgba(58,123,213,0.07)" : "var(--warm-white)", color: filter === f.val ? "var(--blue)" : "var(--text-secondary)", borderColor: filter === f.val ? "var(--blue)" : "var(--border)" }}>{f.label}</span>
        ))}
      </div>

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden", boxShadow: "var(--shadow-sm)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg,#EBF4FF,#E6F7F4)" }}>
                {["Code", "Party Name", "Roles", "City / State", "GSTIN", "Billing Branch", "Cycle", "Status", "Actions"].map(h => (
                  <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-secondary)", borderBottom: "1.5px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const stateObj = states.find(s => String(s.State_id) === String(r.state));
                const stateName = stateObj ? stateObj.State_Name : r.state;
                
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "11px 14px" }}><span className="td-badge badge-blue" style={{ background: "rgba(58,123,213,0.1)", color: "var(--blue)", padding: "3px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>{r.code}</span></td>
                    <td style={{ padding: "11px 14px" }}>
                      <strong style={{ fontSize: 13.5, color: "var(--text-primary)" }}>{r.name}</strong>
                      <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>{r.contact || ""}{r.mobile ? " · " + r.mobile : ""}</div>
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      {r.billing ? <span className="td-badge" style={{ background: "rgba(58,123,213,0.1)", color: "var(--blue)", padding: "3px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>💳 Billing</span> : <span className="td-badge" style={{ background: "rgba(249,168,37,0.12)", color: "#c67c00", padding: "3px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>🏢 Party</span>}
                    </td>
                    <td style={{ padding: "11px 14px", color: "var(--text-muted)", fontSize: 12.5 }}>{r.city || "—"}{stateName ? ", " + stateName : ""}</td>
                    <td style={{ padding: "11px 14px", color: "var(--text-muted)", fontSize: 12 }}>{r.gstin || "—"}</td>
                    <td style={{ padding: "11px 14px" }}>
                      <span className="td-badge" style={{ background: "rgba(58,123,213,0.1)", color: "var(--blue)", padding: "3px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>{r.code}</span>
                    </td>
                    <td style={{ padding: "11px 14px", color: "var(--text-muted)", fontSize: 12.5 }}>{r.billing && r.billingCycle ? r.billingCycle.charAt(0).toUpperCase() + r.billingCycle.slice(1) : "—"}</td>
                    <td style={{ padding: "11px 14px" }}>{statusBadge(r.status)}</td>
                    <td style={{ padding: "11px 14px" }}><ActionButtons onEdit={() => setModal({ open: true, id: r.id })} onToggle={() => handleToggleStatus(r.id)} onDelete={() => handleDelete(r.id)} status={r.status} rights={rights} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", fontSize: 12.5, color: "var(--text-muted)", background: "var(--sky)" }}>
          {filtered.length} of {parties.length} records
        </div>
      </div>

      {modal.open && (
        <PartyModal modal={modal} onClose={() => setModal({ open: false, id: null })} onSave={handleSave} parties={parties} states={states} branches={branches} channels={channels} events={events} mapPartyRecord={mapPartyRecord} />
      )}
    </div>
  );
}